import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useScenarioScore } from "./useScenarioScore";
import { useScenarios } from "./useScenarios";
import LeverEditor from "./LeverEditor";
import ScenarioResultCards from "./ScenarioResultCards";

const INPUT_CLS =
  "rounded-ctrl border border-card-bd px-2 py-1 text-sm text-text-2 focus:outline-none focus:ring-2 focus:ring-primary";
const PRIMARY_BTN_CLS =
  "inline-flex items-center rounded-ctrl bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BTN_CLS =
  "inline-flex items-center rounded-ctrl border border-card-bd px-3 py-1.5 text-sm font-medium text-text-2 transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60";

// useDebounced returns `value` after it has been stable for `delay` ms, so the
// scoring query only re-runs once the user pauses editing levers.
function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ScenarioPlanner is the decision surface for the Forecast Lab "Scenario" tab:
// a lever editor (left) and baseline-vs-scenario result cards (right). The
// baseline-vs-scenario chart is intentionally deferred until the score response
// carries a series. The `scope` prop is accepted for parity with sibling tabs.
export default function ScenarioPlanner({ scope: _scope }) {
  const [levers, setLevers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const debouncedLevers = useDebounced(levers, 300);
  const period = new Date().toISOString().slice(0, 7);

  // Scores once on mount with empty levers (→ baseline) so data.budgets is
  // available to populate the budget picker.
  const { data, isLoading, error } = useScenarioScore(period, debouncedLevers);

  const { list, create, update, remove } = useScenarios();
  const saved = list.data || [];

  // loadScenario hydrates the editor from a saved record (or resets to a new,
  // empty scenario when the blank option is chosen).
  function loadScenario(id) {
    if (!id) {
      setSelectedId(null);
      setName("");
      setLevers([]);
      return;
    }
    const scenario = saved.find((s) => String(s.id) === String(id));
    if (!scenario) return;
    setSelectedId(scenario.id);
    setName(scenario.name || "");
    setLevers(scenario.adjustments || []);
  }

  function handleSave() {
    if (selectedId) {
      update.mutate({ id: selectedId, name, adjustments: levers });
    } else {
      create.mutate(
        { name: name || "Untitled", adjustments: levers },
        // create's mutationFn returns the created record; select it so the next
        // Save updates in place rather than creating a duplicate.
        { onSuccess: (created) => created && setSelectedId(created.id) }
      );
    }
  }

  function handleNew() {
    setSelectedId(null);
    setName("");
    setLevers([]);
  }

  function handleDelete() {
    if (!selectedId) return;
    remove.mutate(selectedId, {
      onSuccess: () => {
        setSelectedId(null);
        setName("");
        setLevers([]);
      },
    });
  }

  const saving = create.isLoading || update.isLoading;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <h2 className="text-base font-semibold text-text">
          Scenario Planner
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Saved scenario"
            value={selectedId == null ? "" : String(selectedId)}
            onChange={(e) => loadScenario(e.target.value)}
            className={INPUT_CLS}
          >
            <option value="">— New scenario —</option>
            {saved.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            aria-label="Scenario name"
            placeholder="Scenario name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${INPUT_CLS} w-40`}
          />
          <button
            type="button"
            aria-label="Save scenario"
            onClick={handleSave}
            disabled={saving}
            className={PRIMARY_BTN_CLS}
          >
            Save
          </button>
          <button
            type="button"
            aria-label="New scenario"
            onClick={handleNew}
            className={SECONDARY_BTN_CLS}
          >
            New
          </button>
          {selectedId && (
            <button
              type="button"
              aria-label="Delete scenario"
              onClick={handleDelete}
              disabled={remove.isLoading}
              className={SECONDARY_BTN_CLS}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <LeverEditor
            levers={levers}
            onChange={setLevers}
            budgets={data?.budgets || []}
          />
        </div>
        <div>
          {error ? (
            <p className="text-sm text-danger">
              Couldn’t score this scenario. Try again.
            </p>
          ) : !data && isLoading ? (
            <p className="text-sm text-text-3">Scoring…</p>
          ) : (
            <ScenarioResultCards result={data} />
          )}
        </div>
      </div>
    </div>
  );
}

ScenarioPlanner.propTypes = {
  /** "insights" or "allocation"; threaded by the tab, unused in v1. */
  scope: PropTypes.string,
};

ScenarioPlanner.defaultProps = {
  scope: "insights",
};
